import { DragEndEvent } from "@dnd-kit/core";
import { EditorApi } from "../../api/editor-api";
import { BackgroundMusicApi } from "../../api/background-music-api";
import { SourceImageApi } from "../../api/source-image-api";
import { AnimatedImageApi } from "../../api/animated-image-api";
import type { Set, Get } from "./editor-store";
import { arrayMove } from "@dnd-kit/sortable";
import { CompositionDto, VideoDto, VoiceOverDto } from "@repo/api/editor";
import {
  BackgroundMusicDto,
  UploadBackgroundMusicDto,
} from "@repo/api/background-music";
import {
  AnimatedImageDto,
  CameraMovement,
  CreateAnimatedImageResponseDto,
  CreateCustomInputAnimatedImageDto,
  InitializeSlowMotionDto,
} from "@repo/api/animated-image";
import { AssetMediaDto } from "@repo/api/asset-media";
import { FontDto } from "@repo/api/font";
import { runConcurrent } from "@repo/api/utils/concurrency";
import {
  BackgroundMusicView,
  CropperState,
  EditorPanelState,
  FontDialogView,
  findElementByExternalId,
  resolveAnimationProvider,
  TimelineItemAnimationForm,
  OverlaysTabState,
  VideoPreviewState,
  VoiceOverForm,
  VoiceoverTabState,
  filterTimelineItemsToAnimate,
  resolveEndImageExternalId,
} from "./editor-state";
import { AnalyticsApi } from "../../api/analytics-api";
import { SyncManager } from "@/lib/sync-manager";
import Toast from "@/lib/components/Toast";
import { debounce, isActiveElementInput, round } from "@/lib/utils";
import {
  getAssetMediaExternalIds,
  getCompositionExternalIds,
  getElementsByScene,
  getElementsByAspectRatio,
  getFontExternalIds,
  getResolution,
  isAnimating,
  isProcessingSlowMotion,
  isSourceImageProcessing,
  parseAspectRatio,
  setCompositionAnimatedImage,
  isProcessingUploadedVideo,
  createCompositionBackgroundMusic,
  getAllElements,
} from "@repo/api/utils/composition";
import { teamQueryKeys } from "@/lib/queries/team-queries";
import { fontQueryKeys } from "@/lib/queries/font-queries";
import {
  backgroundMusicQueryKeys,
  removeBackgroundMusicFromCaches,
} from "@/lib/queries/background-music-queries";
import { queryClient } from "@/lib/queries/query-client";
import {
  CompositionTimings,
  EndSceneTiming,
  findElementTiming,
  getCompositionTimings,
  MainSceneTiming,
} from "@repo/video-engine-i2v/utils/timing";
import {
  ElementDto,
  ElementTypographyDto,
  SceneTypeDto,
  EndSceneDto,
  StartSceneDto,
  OverlayTemplateDto,
} from "@repo/api/overlays";
import { PlayerRef } from "@remotion/player";
import { ThirdPartyApi } from "@/lib/api/third-party-api";
import { VoiceActorConfig } from "@repo/api/voice-actor";
import { voiceOverQueryKeys } from "@/lib/queries/voice-over-queries";
import { AssetMediaApi } from "@/lib/api/asset-media-api";
import { CreativeProjectTimelineItem } from "@repo/api/creative-project";
import {
  ImageAnalysisResult,
  CreateCustomInputSourceImageDto,
  SourceImageDto,
} from "@repo/api/source-image";
import { sourceImageQueryKeys } from "@/lib/queries/source-image-queries";
import { animatedImageQueryKeys } from "@/lib/queries/animated-image-queries";
import { CreativeProjectState } from "./creative-project-state";
import { SourceImageKey } from "@/features/Editor/CreativeProject/creative-project-hooks";
import { CreativeElementDto } from "@repo/api/creative-element";
import { parseAnimatedImagePromptType } from "@repo/api/utils/prompt-type";
import { getAnimatedImageDurationForComposition } from "@/features/Editor/ImagesTab/QuickAnimations/quick-animation-catalog";
import { syncMainSceneTimelineToBeat } from "@/features/Editor/MusicTab/BeatSyncDialog/beat-sync-clips";
import { getVoiceOverOverflowMs } from "@/features/Editor/VoiceoverTab/voiceover-overflow";

export class EditorService {
  constructor(set: Set, get: Get) {
    this.set = set;
    this.get = get;
  }

  private set: Set;
  private get: Get;
  private currentFrame: number = 0;
  private playerRef: React.RefObject<PlayerRef> | null = null;
  private isDestroyed = false;
  private compositionHistory: CompositionDto[] = [];
  private compositionHistoryIndex: number = 0;
  private skipNextCompositionChange: boolean = false;
  private latestCompositionChange = Date.now();
  private lastTextTypography: Pick<
    ElementTypographyDto,
    "fontSize" | "fontExternalId" | "fontWeight" | "fontStyle"
  > | null = null;

  private incompleteImagesSyncManager = new SyncManager(async () => {
    const composition = this.get().video?.compositionJson;
    if (!composition) return;

    const teamExternalId = this.get().teamExternalId;

    const incompleteAnimatedImageExternalIds = Object.values(
      this.get().animatedImages,
    )
      .filter(
        (animatedImage) =>
          isAnimating(animatedImage) ||
          isProcessingSlowMotion(animatedImage) ||
          isProcessingUploadedVideo(animatedImage),
      )
      .map((animatedImage) => animatedImage.externalId);

    if (incompleteAnimatedImageExternalIds.length > 0) {
      const refreshedAnimatedImages =
        await AnimatedImageApi.getAnimatedImagesByExtIds(
          teamExternalId,
          incompleteAnimatedImageExternalIds,
        );

      this.handleNewFailedAnimatedImages(refreshedAnimatedImages);

      this.set((state) => {
        state.animatedImages = {
          ...state.animatedImages,
          ...refreshedAnimatedImages,
        };
      });
    }

    const incompleteSourceImageExternalIds = Object.values(
      this.get().sourceImages,
    )
      .filter((sourceImage) => isSourceImageProcessing(sourceImage))
      .map((sourceImage) => sourceImage.externalId);

    if (incompleteSourceImageExternalIds.length > 0) {
      const refreshedSourceImages =
        await SourceImageApi.getSourceImagesByExtIds(
          teamExternalId,
          incompleteSourceImageExternalIds,
        );

      this.handleNewFailedSourceImages(refreshedSourceImages);

      this.set((state) => {
        state.sourceImages = {
          ...state.sourceImages,
          ...refreshedSourceImages,
        };
      });
    }
  });

  private videoSyncManager = new SyncManager(async () => {
    const state = this.get();

    try {
      await EditorApi.updateVideo(
        state.teamExternalId,
        state.videoExternalId,
        state.video!,
      );
    } catch (error) {
      Toast.error("Failed to sync video to server");
      throw error;
    }
  });

  private refreshIntervalId: number | null = null;

  initialize = async (teamExternalId: string, videoExternalId: string) => {
    const isInitialized = !!this.get().videoExternalId;

    if (isInitialized) {
      console.error("EditorService already initialized");
    } else {
      await this.setupInitialState(teamExternalId, videoExternalId);
    }

    this.startRefreshInterval();
    document.addEventListener("keydown", this.undoCompositionChange);
    document.addEventListener("keydown", this.redoCompositionChange);
  };

  private setupInitialState = async (
    teamExternalId: string,
    videoExternalId: string,
  ) => {
    const cachedState = this.getCachedState(videoExternalId, teamExternalId);

    if (cachedState) {
      this.set((state) => ({
        ...state,
        ...cachedState,
      }));
    } else {
      this.set((state) => {
        state.videoExternalId = videoExternalId;
        state.teamExternalId = teamExternalId;
      });

      const [video, voiceActors, thirdPartyEditorInfo] = await Promise.all([
        EditorApi.getVideo(teamExternalId, videoExternalId),
        EditorApi.getVoiceActors(teamExternalId),
        ThirdPartyApi.getThirdPartyEditorInfo(teamExternalId, videoExternalId),
      ]);

      const {
        sourceImageExternalIds,
        animatedImageExternalIds,
        voiceOverExternalIds,
        backgroundMusicExternalIds,
      } = getCompositionExternalIds(video.compositionJson);

      const fontExternalIds = [
        ...getFontExternalIds(video.compositionJson),
        video.defaultFontExternalId,
      ];

      const [
        sourceImages,
        animatedImages,
        compositionFonts,
        voiceOvers,
        backgroundMusics,
      ] = await Promise.all([
        SourceImageApi.getSourceImagesByExtIds(
          teamExternalId,
          sourceImageExternalIds,
        ),
        AnimatedImageApi.getAnimatedImagesByExtIds(
          teamExternalId,
          animatedImageExternalIds,
        ),
        EditorApi.getFontsByExternalIds(teamExternalId, fontExternalIds),
        EditorApi.getVoiceOversByExtIds(teamExternalId, voiceOverExternalIds),
        BackgroundMusicApi.getBackgroundMusicsByExtIds(
          teamExternalId,
          backgroundMusicExternalIds,
        ),
      ]);

      const isImageSelectorDialogOpen =
        video.compositionJson.mainSceneTimeline.length === 0 &&
        video.hasOriginSourceImages;

      const currentVoiceOverExternalId =
        video.compositionJson.voiceOver?.externalId;
      const currentVoiceOver = voiceOvers.find(
        (voiceOver) => voiceOver.externalId === currentVoiceOverExternalId,
      );

      this.set((state) => {
        state.isInitialized = true;
        state.video = video;
        state.sourceImages = { ...state.sourceImages, ...sourceImages };
        state.animatedImages = { ...state.animatedImages, ...animatedImages };
        state.voiceOvers = voiceOvers;
        state.backgroundMusics = Object.fromEntries(
          backgroundMusics.map((track) => [track.externalId, track]),
        );
        state.voiceActors = voiceActors;
        state.compositionFonts = compositionFonts;
        state.thirdPartyEditorInfo = thirdPartyEditorInfo;
        state.sourceImageSelectorDialogState.isOpen = isImageSelectorDialogOpen;

        if (currentVoiceOver) {
          state.voiceOverForm.script = currentVoiceOver.script;
          state.voiceOverForm.voiceActorExternalId =
            currentVoiceOver.voiceActorExternalId;
        }
      });
    }

    AnalyticsApi.logEditorOpened(teamExternalId);
  };

  getCacheKey = (videoExternalId: string, teamExternalId: string) => {
    return `editor-state-${videoExternalId}-${teamExternalId}`;
  };

  getCachedState = (videoExternalId: string, teamExternalId: string) => {
    const cachedState = sessionStorage.getItem(
      this.getCacheKey(videoExternalId, teamExternalId),
    );

    if (!cachedState) {
      return null;
    }

    sessionStorage.removeItem(
      this.getCacheKey(videoExternalId, teamExternalId),
    );

    try {
      return JSON.parse(cachedState);
    } catch (error) {
      console.error("Failed to parse cached state", error);
      return null;
    }
  };

  saveStateToCache = () => {
    const state = this.get();

    if (
      !state.videoExternalId ||
      !state.teamExternalId ||
      !state.isInitialized
    ) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { service, creditDialogState, ...filteredState } = state;

    sessionStorage.setItem(
      this.getCacheKey(state.videoExternalId, state.teamExternalId),
      JSON.stringify(filteredState),
    );
  };

  setCurrentVoiceOver = (voiceOverExternalId: string) => {
    const voiceOver = this.get().voiceOvers.find(
      (vo) => vo.externalId === voiceOverExternalId,
    );

    if (!voiceOver) {
      console.error("VoiceOver not found");
      return;
    }

    this.set((state) => {
      if (state.video) {
        const existing = state.video.compositionJson.voiceOver;
        state.video.compositionJson.voiceOver = {
          externalId: voiceOverExternalId,
          volume: existing?.volume,
          startSilenceMs: existing?.startSilenceMs,
        };
        state.voiceOverForm.script = voiceOver.script;
        state.voiceOverForm.voiceActorExternalId =
          voiceOver.voiceActorExternalId;
      }
    });

    this.syncVideoDebounced();
  };

  clearVoiceOver = () => {
    this.set((state) => {
      if (state.video) {
        state.video.compositionJson.voiceOver = undefined;
      }
    });

    this.syncVideoDebounced();
  };

  getCurrentVoiceOver = () => {
    const state = this.get();
    const compositionVoiceOver = state.video?.compositionJson.voiceOver;
    const voiceOvers = state.voiceOvers;

    return compositionVoiceOver
      ? (voiceOvers.find(
          (vo) => vo.externalId === compositionVoiceOver.externalId,
        ) ?? null)
      : null;
  };

  handleImageDrag = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const items = this.get().video?.compositionJson.mainSceneTimeline;

    if (!items) {
      return;
    }

    const oldIndex = items.findIndex((item) => item.externalId === active.id);
    const newIndex = items.findIndex((item) => item.externalId === over.id);

    const newItems = arrayMove(items, oldIndex, newIndex);

    this.set((state) => {
      state.video!.compositionJson.mainSceneTimeline = newItems;
    });

    this.syncVideoDebounced();
  };

  startRefreshInterval = () => {
    if (this.isDestroyed) {
      return;
    }

    this.refreshIntervalId = setInterval(() => {
      this.incompleteImagesSyncManager.sync();
    }, 5000);
  };

  setSourceImagesAsDeleted = (externalIds: string[]) => {
    this.set((state) => {
      for (const externalId of externalIds) {
        if (state.sourceImages[externalId]) {
          state.sourceImages[externalId].deletedAt = new Date().toISOString();
        }
      }
    });
  };

  setAnimatedImagesAsDeleted = (externalIds: string[]) => {
    this.set((state) => {
      for (const externalId of externalIds) {
        if (state.animatedImages[externalId]) {
          state.animatedImages[externalId].deletedAt = new Date().toISOString();
        }
      }
    });
  };

  uploadSourceImagesSyncComposition = async (
    images: File[],
    onProgress?: (completed: number, total: number) => void,
  ) => {
    const newSourceImages = await this.uploadSourceImages(images, onProgress);
    this.addSourceImagesToComposition(newSourceImages);
  };

  uploadAnimatedImages = async (
    videos: File[],
    onProgress?: (completed: number, total: number) => void,
  ): Promise<AnimatedImageDto[]> => {
    if (videos.length === 0) return [];

    const videoExternalId = this.get().videoExternalId;
    const teamExternalId = this.get().teamExternalId;

    const results = await runConcurrent(
      videos,
      (video) =>
        AnimatedImageApi.uploadAnimatedImage(
          teamExternalId,
          videoExternalId,
          video,
        ),
      { concurrency: 2, retries: 1, onProgress },
    );

    const uploaded = results.filter((r) => r.success).map((r) => r.value);
    const failedCount = results.filter((r) => !r.success).length;

    if (uploaded.length > 0) {
      this.set((state) => {
        for (const { animatedImage, startSourceImage } of uploaded) {
          state.sourceImages[startSourceImage.externalId] = startSourceImage;
          state.animatedImages[animatedImage.externalId] = animatedImage;
        }
      });
    }

    if (failedCount > 0) {
      if (uploaded.length === 0) {
        throw new Error("Failed to upload videos");
      }
      const videosText = failedCount === 1 ? "video" : "videos";
      Toast.error(`${failedCount} ${videosText} failed to upload`);
    }

    queryClient.invalidateQueries({
      queryKey: animatedImageQueryKeys.getByOriginVideoExternalId(
        teamExternalId,
        videoExternalId,
      ),
    });

    queryClient.invalidateQueries({
      queryKey: animatedImageQueryKeys.paged(teamExternalId),
    });

    return uploaded.map((r) => r.animatedImage);
  };

  uploadAnimatedImagesSyncComposition = async (
    videos: File[],
    onProgress?: (completed: number, total: number) => void,
  ) => {
    const animatedImages = await this.uploadAnimatedImages(videos, onProgress);

    if (animatedImages.length === 0) return;

    this.set((state) => {
      for (const animatedImage of animatedImages) {
        const newItem: CreativeProjectTimelineItem = {
          type: "CREATIVE_PROJECT",
          externalId: crypto.randomUUID(),
          startSourceImageExternalId: animatedImage.startImageExternalId,
          sourceImageExternalIds: [animatedImage.startImageExternalId],
          animatedImageExternalIds: [],
          animatedImageConfigs: {},
        };

        setCompositionAnimatedImage({
          timelineItem: newItem,
          animatedImageExternalId: animatedImage.externalId,
          duration: round(animatedImage.durationMs / 1000, 1),
          volume: animatedImage.hasAudio ? 1 : undefined,
        });

        state.video!.compositionJson.mainSceneTimeline.push(newItem);
      }
    });

    this.syncVideoDebounced();
  };

  addSourceImagesToComposition = (sourceImages: SourceImageDto[]) => {
    const sourceImagesMap = Object.fromEntries(
      sourceImages.map((image) => [image.externalId, image]),
    );

    const newTimelineItems: CreativeProjectTimelineItem[] = sourceImages.map(
      (image) => ({
        type: "CREATIVE_PROJECT",
        externalId: crypto.randomUUID(),
        startSourceImageExternalId: image.externalId,
        sourceImageExternalIds: [image.externalId],
        animatedImageExternalIds: [],
        animatedImageConfigs: {},
      }),
    );

    this.set((state) => {
      state.sourceImages = { ...state.sourceImages, ...sourceImagesMap };
      state.video!.compositionJson.mainSceneTimeline = [
        ...state.video!.compositionJson.mainSceneTimeline,
        ...newTimelineItems,
      ];
    });

    this.syncVideoDebounced();

    const newImageIds = sourceImages.map((img) => img.externalId);

    if (newImageIds.length > 0) {
      SourceImageApi.analyzeImages(this.get().teamExternalId, {
        sourceImageExternalIds: newImageIds,
      }).catch((error) => {
        console.error("Failed to start image analysis:", error);
      });
    }
  };

  addAnimatedImagesToComposition = async (
    animatedImages: AnimatedImageDto[],
  ) => {
    if (animatedImages.length === 0) return;

    const teamExternalId = this.get().teamExternalId;

    const existingSourceImages = this.get().sourceImages;
    const requiredSourceImageIds = new Set<string>();

    for (const animatedImage of animatedImages) {
      requiredSourceImageIds.add(animatedImage.startImageExternalId);

      if (animatedImage.endImageExternalId) {
        requiredSourceImageIds.add(animatedImage.endImageExternalId);
      }
    }

    const missingSourceImageIds = Array.from(requiredSourceImageIds).filter(
      (id) => !existingSourceImages[id],
    );

    let fetchedSourceImages: Record<string, SourceImageDto> = {};
    if (missingSourceImageIds.length > 0) {
      try {
        fetchedSourceImages = await SourceImageApi.getSourceImagesByExtIds(
          teamExternalId,
          missingSourceImageIds,
        );
      } catch (error) {
        console.error(
          "Failed to fetch source images for animated images:",
          error,
        );
        Toast.error("Failed to load animation source images");
        return;
      }
    }

    const animatedImagesMap = Object.fromEntries(
      animatedImages.map((animatedImage) => [
        animatedImage.externalId,
        animatedImage,
      ]),
    );

    const newTimelineItems: CreativeProjectTimelineItem[] = animatedImages.map(
      (animatedImage) => {
        const timelineItem: CreativeProjectTimelineItem = {
          type: "CREATIVE_PROJECT",
          externalId: crypto.randomUUID(),
          startSourceImageExternalId: animatedImage.startImageExternalId,
          endSourceImageExternalId:
            animatedImage.endImageExternalId || undefined,
          sourceImageExternalIds: [
            animatedImage.startImageExternalId,
            animatedImage.endImageExternalId,
          ].filter(Boolean) as string[],
          animatedImageExternalIds: [],
          animatedImageConfigs: {},
        };

        setCompositionAnimatedImage({
          timelineItem: timelineItem,
          animatedImageExternalId: animatedImage.externalId,
        });

        return timelineItem;
      },
    );

    this.set((state) => {
      state.sourceImages = {
        ...state.sourceImages,
        ...fetchedSourceImages,
      };
      state.animatedImages = {
        ...state.animatedImages,
        ...animatedImagesMap,
      };
      state.video!.compositionJson.mainSceneTimeline = [
        ...state.video!.compositionJson.mainSceneTimeline,
        ...newTimelineItems,
      ];
    });

    this.syncVideoDebounced();
  };

  uploadSourceImages = async (
    images: File[],
    onProgress?: (completed: number, total: number) => void,
  ): Promise<SourceImageDto[]> => {
    const videoExternalId = this.get().videoExternalId;
    const teamExternalId = this.get().teamExternalId;

    const results = await runConcurrent(
      images,
      (image) =>
        SourceImageApi.uploadImage(teamExternalId, videoExternalId, image),
      { concurrency: 4, retries: 2, onProgress },
    );

    const sourceImages = results.filter((r) => r.success).map((r) => r.value);
    const failedCount = results.filter((r) => !r.success).length;

    if (sourceImages.length > 0) {
      const sourceImagesMap = Object.fromEntries(
        sourceImages.map((image) => [image.externalId, image]),
      );

      this.set((state) => {
        state.sourceImages = { ...state.sourceImages, ...sourceImagesMap };
      });
    }

    if (failedCount > 0) {
      if (sourceImages.length === 0) {
        throw new Error("Failed to upload images");
      }
      const imagesText = failedCount === 1 ? "image" : "images";
      Toast.error(`${failedCount} ${imagesText} failed to upload`);
    }

    queryClient.invalidateQueries({
      queryKey: sourceImageQueryKeys.getByOriginVideoExternalId(
        teamExternalId,
        videoExternalId,
      ),
    });

    queryClient.invalidateQueries({
      queryKey: sourceImageQueryKeys.paged(teamExternalId),
    });

    return sourceImages;
  };

  createVoiceOver = async (script: string, voiceActorExternalId: string) => {
    const voiceOver = await EditorApi.createVoiceOver(
      this.get().teamExternalId,
      this.get().videoExternalId,
      {
        script,
        voiceActorExternalId,
      },
    );

    const voiceOvers = [voiceOver, ...this.get().voiceOvers];

    this.set((state) => {
      const composition = state.video!.compositionJson;
      const existing = composition.voiceOver;

      composition.voiceOver = {
        externalId: voiceOver.externalId,
        volume: existing?.volume,
        startSilenceMs: existing?.startSilenceMs,
      };
      composition.voiceOverExternalIds = [
        ...(composition.voiceOverExternalIds ?? []),
        voiceOver.externalId,
      ];
      state.voiceOvers = voiceOvers;
    });

    this.syncVideoDebounced();
    queryClient.invalidateQueries({
      queryKey: voiceOverQueryKeys.getPreviouslyUsedVoiceActors(
        this.get().teamExternalId,
      ),
    });

    return voiceOver;
  };

  getVoiceOverOverflowMs = (durationMs: number) => {
    const { video, animatedImages } = this.get();
    if (!video) return 0;

    return getVoiceOverOverflowMs(
      video.compositionJson,
      animatedImages,
      durationMs,
    );
  };

  deleteVideo = async () => {
    const teamExternalId = this.get().teamExternalId;
    const videoExternalId = this.get().videoExternalId;

    const deletedVideo = await EditorApi.deleteVideo(
      teamExternalId,
      videoExternalId,
    );

    if (deletedVideo.deletedAt) {
      this.set((state) => {
        state.isInitialized = false;
        state.video = null;
        state.sourceImages = {};
        state.animatedImages = {};
        state.voiceOvers = [];
        state.voiceActors = [];
        state.assetMedias = {};
      });

      return deletedVideo;
    }
  };

  syncVideoDebounced = debounce(async () => {
    await this.videoSyncManager.sync();
  }, 500);

  updateElement = (
    elementExternalId: string,
    updater: (currentElement: ElementDto) => void,
    sync: boolean = true,
    revealHidden: boolean = true,
  ) => {
    this.set((state) => {
      const element = findElementByExternalId(state, elementExternalId);

      if (!element) {
        console.error("Element not found");
        return;
      }

      updater(element);

      if (revealHidden && element.isHidden) {
        element.isHidden = false;
      }
    });

    if (sync) {
      this.syncVideoDebounced();
    }
  };

  toggleElementsVisibility = (
    elementExternalIds: string[],
    sync: boolean = true,
  ) => {
    if (elementExternalIds.length === 0) {
      return;
    }

    this.set((state) => {
      const elements = elementExternalIds
        .map((id) => findElementByExternalId(state, id))
        .filter((element): element is ElementDto => element != null);

      if (elements.length === 0) {
        return;
      }

      // Only hide when every selected element is visible; a mixed or
      // fully-hidden selection is revealed first.
      const nextHidden = elements.every((element) => !element.isHidden);

      for (const element of elements) {
        element.isHidden = nextHidden;
      }
    });

    if (sync) {
      this.syncVideoDebounced();
    }
  };

  deleteElement = (
    elementExternalId: string,
    sceneType: SceneTypeDto,
    sync: boolean = true,
  ) => {
    this.set((state) => {
      const elements = getElementsByAspectRatio(
        state.video!.compositionJson,
        sceneType,
      );

      if (!elements) {
        return;
      }

      const index = elements.findIndex(
        (element) => element.externalId === elementExternalId,
      );

      if (index !== -1) {
        elements.splice(index, 1);
      }
    });

    if (sync) {
      this.syncVideoDebounced();
    }
  };

  updateEditorPanelState = (
    updater: (currentState: EditorPanelState) => void,
  ) => {
    this.set((state) => {
      updater(state.editorPanelState);
    });
  };

  updateOverlaysTabState = (
    updater: (currentState: OverlaysTabState) => void,
  ) => {
    this.set((state) => {
      updater(state.overlaysTabState);
    });
  };

  setSelectedElementIds = (elementExternalIds: string[]) => {
    this.set((state) => {
      state.selectedElementIds = elementExternalIds;
    });
  };

  updateVoiceoverTabState = (
    updater: (currentState: VoiceoverTabState) => void,
  ) => {
    this.set((state) => {
      updater(state.voiceoverTabState);
    });
  };

  openCreativeProjectSheet = (creativeProjectExternalId: string) => {
    this.ensureCreativeProjectState(creativeProjectExternalId);

    this.set((state) => {
      state.creativeProjectSheetState.isSheetOpen = true;
      state.creativeProjectSheetState.currentCreativeProjectExternalId =
        creativeProjectExternalId;

      const itemExists = !!state.animationForm.items[creativeProjectExternalId];
      if (itemExists) {
        state.animationForm.items[creativeProjectExternalId] = {
          promptType: null,
        };
      }
    });
  };

  closeCreativeProjectSheet = () => {
    this.set((state) => {
      // currentCreativeProjectExternalId is kept to maintain the state during the transition
      state.creativeProjectSheetState.isSheetOpen = false;
    });
  };

  replaceCurrentTimelineItemSourceImage = (
    sourceImageKey: SourceImageKey,
    sourceImage: SourceImageDto,
  ) => {
    const timelineItem =
      this.get().service.getCreativeProjectSheetTimelineItem();
    if (!timelineItem) return;

    this.set((state) => {
      state.sourceImages[sourceImage.externalId] = sourceImage;

      const item = state.video?.compositionJson.mainSceneTimeline.find(
        (i) => i.externalId === timelineItem.externalId,
      );

      if (item) {
        item[sourceImageKey] = sourceImage.externalId;

        if (!item.sourceImageExternalIds.includes(sourceImage.externalId)) {
          item.sourceImageExternalIds = [
            sourceImage.externalId,
            ...item.sourceImageExternalIds,
          ];
        }
      }
    });

    this.syncVideoDebounced();
  };

  updateCreativeProjectState = (
    creativeProjectExternalId: string,
    updater: (currentState: CreativeProjectState) => void,
  ) => {
    this.ensureCreativeProjectState(creativeProjectExternalId);

    this.set((state) => {
      updater(
        state.creativeProjectSheetState.creativeProjects[
          creativeProjectExternalId
        ]!,
      );
    });
  };

  getCurrentCreativeProjectState = (): CreativeProjectState | null => {
    const state = this.get();
    const externalId =
      state.creativeProjectSheetState.currentCreativeProjectExternalId;

    if (!externalId) return null;

    return state.creativeProjectSheetState.creativeProjects[externalId] ?? null;
  };

  updateCurrentCreativeProjectState = (
    updater: (currentState: CreativeProjectState) => void,
  ) => {
    const externalId =
      this.get().creativeProjectSheetState.currentCreativeProjectExternalId;

    if (!externalId) return;

    this.updateCreativeProjectState(externalId, updater);
  };

  storeCreativeElement = (element: CreativeElementDto) => {
    this.set((state) => {
      state.creativeElements[element.externalId] = element;
    });
  };

  private ensureCreativeProjectState = (creativeProjectExternalId: string) => {
    this.set((state) => {
      let creativeProjectState: CreativeProjectState | undefined =
        state.creativeProjectSheetState.creativeProjects[
          creativeProjectExternalId
        ];

      if (!creativeProjectState) {
        creativeProjectState = {
          animationForm: {
            prompt: "",
            duration: 5,
          },
          startImageEditForm: {
            prompt: "",
            numGeneratedImages: 1,
            elementExternalIds: [],
          },
          endImageEditForm: {
            prompt: "",
            numGeneratedImages: 1,
            elementExternalIds: [],
          },
        };

        state.creativeProjectSheetState.creativeProjects[
          creativeProjectExternalId
        ] = creativeProjectState;
      }
    });
  };

  createCustomInputAnimatedImage = async (
    data: Omit<
      CreateCustomInputAnimatedImageDto,
      "originVideoExternalId" | "startImageExternalId" | "provider"
    >,
  ) => {
    const timelineItem =
      this.get().service.getCreativeProjectSheetTimelineItem();

    if (!timelineItem?.startSourceImageExternalId) {
      throw new Error("Timeline item or start source image not found");
    }

    let animatedImage;

    try {
      animatedImage = await AnimatedImageApi.createCustomInputAnimatedImage(
        this.get().teamExternalId,
        {
          ...data,
          originVideoExternalId: this.get().videoExternalId,
          startImageExternalId: timelineItem.startSourceImageExternalId,
          endImageExternalId: timelineItem.endSourceImageExternalId,
          provider: timelineItem.endSourceImageExternalId
            ? "amplifiles/perspective-3.0"
            : "amplifiles/perspective-3.1", // Perspective 3.1 does not support end images
        },
      );
    } catch (error: any) {
      const notEnoughCredits =
        error.response?.data?.errorCode === "NOT_ENOUGH_CREDITS";

      if (notEnoughCredits) {
        await this.refreshCreditBalance();
        this.closeCreativeProjectSheet();
        this.setIsCreditDialogOpen(true);
      }

      throw error;
    }

    await this.refreshCreditBalance();

    this.set((state) => {
      state.animatedImages[animatedImage.externalId] = animatedImage;

      const item = state.video?.compositionJson.mainSceneTimeline.find(
        (i) => i.externalId === timelineItem.externalId,
      );

      if (item) {
        delete state.animationForm.items[timelineItem.externalId];
        setCompositionAnimatedImage({
          timelineItem: item,
          animatedImageExternalId: animatedImage.externalId,
          duration: data.customInput.duration,
        });
      }
    });

    this.syncVideoDebounced();

    return animatedImage;
  };

  createCustomInputSourceImage = async (
    sourceImageKey: SourceImageKey,
    data: Omit<
      CreateCustomInputSourceImageDto,
      "originVideoExternalId" | "referenceImageExternalId"
    >,
  ) => {
    const timelineItem =
      this.get().service.getCreativeProjectSheetTimelineItem();

    const referenceImageExternalId = timelineItem?.[sourceImageKey];

    if (!referenceImageExternalId) {
      throw new Error(`Timeline item or ${sourceImageKey} not found`);
    }

    let sourceImage;

    try {
      sourceImage = await SourceImageApi.createCustomInputSourceImage(
        this.get().teamExternalId,
        {
          ...data,
          originVideoExternalId: this.get().videoExternalId,
          referenceImageExternalId,
        },
      );
    } catch (error: any) {
      const notEnoughCredits =
        error.response?.data?.errorCode === "NOT_ENOUGH_CREDITS";

      if (notEnoughCredits) {
        await this.refreshCreditBalance();
        this.closeCreativeProjectSheet();
        this.setIsCreditDialogOpen(true);
      }

      throw error;
    }

    await this.refreshCreditBalance();

    this.set((state) => {
      state.sourceImages[sourceImage.externalId] = sourceImage;

      const item = state.video?.compositionJson.mainSceneTimeline.find(
        (i) => i.externalId === timelineItem.externalId,
      );

      if (item) {
        item[sourceImageKey] = sourceImage.externalId;
        item.sourceImageExternalIds = [
          sourceImage.externalId,
          ...item.sourceImageExternalIds,
        ];
      }
    });

    this.syncVideoDebounced();

    return sourceImage;
  };

  getCreativeProjectSheetTimelineItem = () => {
    const mainSceneTimeline =
      this.get().video?.compositionJson.mainSceneTimeline;
    const currentCreativeProjectExternalId =
      this.get().creativeProjectSheetState.currentCreativeProjectExternalId;

    if (!mainSceneTimeline || !currentCreativeProjectExternalId) {
      return null;
    }

    const timelineItem = mainSceneTimeline.find(
      (item) => item.externalId === currentCreativeProjectExternalId,
    );

    return timelineItem;
  };

  getSourceImageByExternalId = (externalId: string | undefined) => {
    if (!externalId) return null;

    const sourceImages = this.get().sourceImages;
    return sourceImages[externalId] ?? null;
  };

  setPreviewVoiceOver = (voiceOver: VoiceOverDto) => {
    this.set((state) => {
      const isSameVoiceOver =
        state.previewVoiceOver?.externalId === voiceOver.externalId;

      if (isSameVoiceOver) {
        state.previewVoiceOver = null;
        state.isPreviewVoiceOverPlaying = false;
      } else {
        state.previewVoiceOver = voiceOver;
        state.isPreviewVoiceOverPlaying = true;
      }
    });
  };

  togglePreviewVoiceOverPlayback = () => {
    this.set((state) => {
      state.isPreviewVoiceOverPlaying = !state.isPreviewVoiceOverPlaying;
    });
  };

  clearPreviewVoiceOver = () => {
    this.set((state) => {
      state.previewVoiceOver = null;
      state.isPreviewVoiceOverPlaying = false;
    });
  };

  setPreviewVoiceOverPlaying = (isPlaying: boolean) => {
    this.set((state) => {
      state.isPreviewVoiceOverPlaying = isPlaying;
    });

    if (isPlaying) {
      const player = this.playerRef?.current;

      if (player && !player.isPlaying()) {
        player.pause();
      }
    }
  };

  updateVoiceOverForm = (updater: (currentForm: VoiceOverForm) => void) => {
    this.set((state) => {
      updater(state.voiceOverForm);
    });
  };

  updateIsEditingVideoName = (isEditing: boolean) => {
    this.set((state) => {
      state.isEditingVideoName = isEditing;
    });
  };

  updateVideo = (updater: (currentVideo: VideoDto) => void) => {
    this.set((state) => {
      updater(state.video!);
    });

    this.syncVideoDebounced();
  };

  updateAnimationForm = (
    updater: (currentAnimationForm: TimelineItemAnimationForm) => void,
  ) => {
    this.set((state) => {
      updater(state.animationForm);
    });
  };

  setCropperState = (cropperState: CropperState | null) => {
    this.set((state) => {
      state.cropperState = cropperState;
    });
  };

  uploadBackgroundMusic = async (
    values: UploadBackgroundMusicDto,
    track: File,
  ) => {
    const backgroundMusic = await BackgroundMusicApi.uploadBackgroundMusic(
      this.get().teamExternalId,
      values,
      track,
    );

    this.set((state) => {
      const composition = state.video!.compositionJson;
      composition.backgroundMusic = createCompositionBackgroundMusic(
        composition,
        backgroundMusic.externalId,
      );

      this.upsertBackgroundMusic(state.backgroundMusics, backgroundMusic);
    });

    queryClient.invalidateQueries({
      queryKey: backgroundMusicQueryKeys.root(),
    });

    this.syncVideoDebounced();
  };

  updateBackgroundMusic = async (
    backgroundMusicExternalId: string,
    values: UploadBackgroundMusicDto,
    track: File | undefined,
  ) => {
    const backgroundMusic = await BackgroundMusicApi.updateBackgroundMusic(
      this.get().teamExternalId,
      backgroundMusicExternalId,
      values,
      track,
    );

    this.set((state) => {
      if (
        state.video?.compositionJson.backgroundMusic?.externalId ===
        backgroundMusicExternalId
      ) {
        state.video!.compositionJson.backgroundMusic =
          createCompositionBackgroundMusic(
            state.video!.compositionJson,
            backgroundMusic.externalId,
          );
      }

      this.upsertBackgroundMusic(state.backgroundMusics, backgroundMusic);
    });

    queryClient.invalidateQueries({
      queryKey: backgroundMusicQueryKeys.root(),
    });

    this.syncVideoDebounced();
  };

  deleteBackgroundMusic = async (backgroundMusicExternalId: string) => {
    await BackgroundMusicApi.deleteBackgroundMusic(
      this.get().teamExternalId,
      backgroundMusicExternalId,
    );

    this.set((state) => {
      if (
        state.video?.compositionJson.backgroundMusic?.externalId ===
        backgroundMusicExternalId
      ) {
        state.video.compositionJson.backgroundMusic = undefined;
      }
    });

    queryClient.setQueriesData(
      { queryKey: backgroundMusicQueryKeys.root() },
      (data) =>
        removeBackgroundMusicFromCaches(data, backgroundMusicExternalId),
    );

    this.syncVideoDebounced();
  };

  areClipDurationsSyncedToBeatSync = (cuttingPointsMs: number[]): boolean => {
    const composition = this.get().video?.compositionJson;

    if (!composition || cuttingPointsMs.length === 0) {
      return false;
    }

    let syncedTimelineItems: CreativeProjectTimelineItem[];

    try {
      syncedTimelineItems = syncMainSceneTimelineToBeat({
        cutPointsMs: cuttingPointsMs,
        composition: JSON.parse(JSON.stringify(composition)),
        animatedImages: this.get().animatedImages,
      });
    } catch {
      return false;
    }

    if (syncedTimelineItems.length !== composition.mainSceneTimeline.length) {
      return false;
    }

    return composition.mainSceneTimeline.every((item, index) => {
      const syncedItem = syncedTimelineItems[index]!;

      const itemDuration = item.animatedImageExternalId
        ? item.animatedImageConfigs[item.animatedImageExternalId]?.duration
        : undefined;

      const syncedItemDuration = syncedItem.animatedImageExternalId
        ? syncedItem.animatedImageConfigs[syncedItem.animatedImageExternalId]
            ?.duration
        : undefined;

      const itemEntryTransition = item.entryTransition?.kind ?? "NONE";
      const syncedItemEntryTransition =
        syncedItem.entryTransition?.kind ?? "NONE";

      return (
        itemDuration === syncedItemDuration &&
        itemEntryTransition === syncedItemEntryTransition
      );
    });
  };

  syncClipDurationsToBeatSync = (cuttingPointsMs: number[]) => {
    const composition = this.get().video?.compositionJson;

    if (!composition) {
      return 0;
    }

    const syncedTimelineItems = syncMainSceneTimelineToBeat({
      cutPointsMs: cuttingPointsMs,
      composition: JSON.parse(JSON.stringify(composition)),
      animatedImages: this.get().animatedImages,
    });

    this.updateVideo((video) => {
      video.compositionJson.mainSceneTimeline = syncedTimelineItems;
    });
  };

  toggleBackgroundMusic = (backgroundMusicExternalId: string) => {
    this.updateVideo((video) => {
      if (
        video.compositionJson.backgroundMusic?.externalId ===
        backgroundMusicExternalId
      ) {
        video.compositionJson.backgroundMusic = undefined;
      } else {
        video.compositionJson.backgroundMusic =
          createCompositionBackgroundMusic(
            video.compositionJson,
            backgroundMusicExternalId,
          );
      }
    });
  };

  selectBackgroundMusic = (track: BackgroundMusicDto) => {
    this.updateVideo((video) => {
      video.compositionJson.backgroundMusic = createCompositionBackgroundMusic(
        video.compositionJson,
        track.externalId,
      );
    });

    this.set((state) => {
      this.upsertBackgroundMusic(state.backgroundMusics, track);
    });
  };

  setBackgroundMusicFavorite = (externalId: string, isFavorite: boolean) => {
    this.set((state) => {
      const track = state.backgroundMusics[externalId];
      if (track) {
        track.isFavorite = isFavorite;
      }
    });
  };

  private upsertBackgroundMusic = (
    backgroundMusics: Record<string, BackgroundMusicDto>,
    track: BackgroundMusicDto,
  ) => {
    backgroundMusics[track.externalId] = track;
  };

  setBackgroundMusicView = (
    view: BackgroundMusicView,
    editingTrackExternalId?: string,
  ) => {
    this.set((state) => {
      state.backgroundMusicDialogState.view = view;
      state.backgroundMusicDialogState.editingTrackExternalId =
        editingTrackExternalId;
    });
  };

  setFontDialogView = (view: FontDialogView) => {
    this.set((state) => {
      state.fontDialogState.view = view;
    });
  };

  getFontByExternalId = (externalId: string) => {
    return this.get().compositionFonts.find(
      (font) => font.externalId === externalId,
    );
  };

  getFontByFamily = (family: string) => {
    return this.get().compositionFonts.find((font) => font.family === family);
  };

  /**
   * Ensures a font selected from the picker is present in `compositionFonts` so
   * the preview's FontContext can resolve it. The full team library lives in a
   * separate React Query cache and is intentionally not stored here.
   */
  addCompositionFont = (font: FontDto) => {
    this.set((state) => {
      if (
        state.compositionFonts.some(
          (existing) => existing.externalId === font.externalId,
        )
      ) {
        return;
      }

      state.compositionFonts = [font, ...state.compositionFonts];
    });
  };

  deleteFont = async (fontExternalId: string) => {
    await EditorApi.deleteFont(this.get().teamExternalId, fontExternalId);

    queryClient.invalidateQueries({
      queryKey: fontQueryKeys.teamFonts(this.get().teamExternalId),
    });
  };

  createVoiceActor = async (config: VoiceActorConfig, isGlobal: boolean) => {
    const voiceActor = await EditorApi.createVoiceActor(
      this.get().teamExternalId,
      config,
      isGlobal,
    );

    this.set((state) => {
      state.voiceActors = [...state.voiceActors, voiceActor];
    });
  };

  updateVoiceActor = async (
    voiceActorExternalId: string,
    isGlobal: boolean,
  ) => {
    const updatedVoiceActor = await EditorApi.updateVoiceActor(
      this.get().teamExternalId,
      voiceActorExternalId,
      isGlobal,
    );

    this.set((state) => {
      const index = state.voiceActors.findIndex(
        (va) => va.externalId === voiceActorExternalId,
      );

      if (index !== -1) {
        state.voiceActors[index] = updatedVoiceActor;
      }
    });
  };

  selectTimelineItemForAnimation = (
    timelineItemExternalId: string,
    selected: boolean,
  ) => {
    if (selected) {
      this.set((state) => {
        state.animationForm.items[timelineItemExternalId] = {
          promptType: "PUSH_IN",
        };
      });
    } else {
      this.set((state) => {
        delete state.animationForm.items[timelineItemExternalId];
      });
    }
  };

  setIsCreditDialogOpen = (
    isCreditDialogOpen: boolean,
    openedFromAnimate = false,
  ) => {
    this.set((state) => {
      state.creditDialogState.isOpen = isCreditDialogOpen;
      state.creditDialogState.openedFromAnimate = openedFromAnimate;
    });
  };

  setIsImageSelectorDialogOpen = (isOpen: boolean) => {
    this.set((state) => {
      state.sourceImageSelectorDialogState.isOpen = isOpen;
    });
  };

  setSourceImage = (sourceImageDto: SourceImageDto) => {
    this.set((state) => {
      state.sourceImages[sourceImageDto.externalId] = sourceImageDto;
    });
  };

  setQuickAnimationsDialogOpen = (
    timelineItemExternalId: string,
    isOpen: boolean,
  ) => {
    this.set((state) => {
      state.quickAnimationsDialogState.openTimelineItemExternalId = isOpen
        ? timelineItemExternalId
        : null;
    });
  };

  animateImages = async (limit?: number) => {
    const timelineItems =
      this.get().video?.compositionJson.mainSceneTimeline ?? [];
    const formItems = this.get().animationForm.items;
    const formProvider = this.get().animationForm.provider;
    const selectedItems = filterTimelineItemsToAnimate(
      timelineItems,
      formItems,
    );
    let itemsToAnimate = selectedItems;

    if (limit !== undefined) {
      itemsToAnimate = Object.fromEntries(
        timelineItems
          .filter((item) => selectedItems[item.externalId])
          .slice(0, limit)
          .map((item) => [item.externalId, selectedItems[item.externalId]!]),
      );
    }

    const data = Object.entries(itemsToAnimate).map(
      ([timelineItemExternalId, itemToAnimate]) => {
        const timelineItem = timelineItems.find(
          (item) => item.externalId === timelineItemExternalId,
        );

        if (!timelineItem) {
          throw new Error(
            `Timeline item not found for ${timelineItemExternalId}`,
          );
        }

        if (itemToAnimate.promptType === null) {
          throw new Error(`Prompt type is null for ${timelineItemExternalId}`);
        }

        const endImageExternalId = resolveEndImageExternalId(
          timelineItem.startSourceImageExternalId!,
          itemToAnimate,
        );

        const provider =
          formProvider ??
          resolveAnimationProvider(
            this.get().animatedImages,
            timelineItem.animatedImageExternalIds,
            timelineItem.startSourceImageExternalId,
            itemToAnimate.promptType,
            endImageExternalId,
          );

        return {
          timelineItemExternalId: timelineItem.externalId,
          startImageExternalId: timelineItem.startSourceImageExternalId!,
          endImageExternalId,
          originVideoExternalId: this.get().videoExternalId,
          promptType: itemToAnimate.promptType,
          motionIntensity: itemToAnimate.motionIntensity,
          provider,
        };
      },
    );

    let newResponses: CreateAnimatedImageResponseDto[];

    try {
      newResponses = await AnimatedImageApi.createAnimatedImages(
        this.get().teamExternalId,
        data,
      );
    } catch (error: any) {
      const notEnoughCredits =
        error.response?.data?.errorCode === "NOT_ENOUGH_CREDITS";

      if (notEnoughCredits) {
        await this.refreshCreditBalance();
        this.setIsCreditDialogOpen(true, true);
        return;
      } else {
        throw error;
      }
    }

    // Re-animation cost depends on existing animations per source image, so
    // any cached credit calculation must be refreshed after creating new ones.
    queryClient.invalidateQueries({
      queryKey: animatedImageQueryKeys.quickAnimationCredits(),
    });

    const newAnimatedImages = newResponses.map((r) => r.animatedImage);

    const newAnimatedImagesMap = Object.fromEntries(
      newAnimatedImages.map((animatedImage) => [
        animatedImage.externalId,
        animatedImage,
      ]),
    );

    const animatedImagesByTimelineItemId = new Map(
      newResponses.map((r) => [r.timelineItemExternalId, r.animatedImage]),
    );

    this.set((state) => {
      state.animatedImages = {
        ...state.animatedImages,
        ...newAnimatedImagesMap,
      };

      state.video?.compositionJson.mainSceneTimeline.forEach((timelineItem) => {
        const newAnimatedImage = animatedImagesByTimelineItemId.get(
          timelineItem.externalId,
        );

        if (newAnimatedImage && !!newAnimatedImage.promptType) {
          const duration = getAnimatedImageDurationForComposition(
            parseAnimatedImagePromptType(newAnimatedImage.promptType),
          );

          setCompositionAnimatedImage({
            timelineItem: timelineItem,
            animatedImageExternalId: newAnimatedImage.externalId,
            duration: duration,
          });
        }
      });

      for (const id of Object.keys(itemsToAnimate)) {
        delete state.animationForm.items[id];
      }
    });

    await this.refreshCreditBalance();

    this.syncVideoDebounced();
  };

  destroy = () => {
    this.isDestroyed = true;

    if (this.refreshIntervalId) {
      clearInterval(this.refreshIntervalId);
    }

    document.removeEventListener("keydown", this.undoCompositionChange);
    document.removeEventListener("keydown", this.redoCompositionChange);
  };

  fetchRealEstateListingImages = async (listingUrl: string) => {
    const teamExternalId = this.get().teamExternalId;
    const videoExternalId = this.get().videoExternalId;

    const response = await EditorApi.fetchRealEstateListingImages(
      teamExternalId,
      videoExternalId,
      listingUrl,
    );

    this.setIsImageSelectorDialogOpen(true);

    return response;
  };

  updateVideoPreviewState = (
    updater: (currentState: VideoPreviewState) => void,
  ) => {
    this.set((state) => {
      updater(state.videoPreviewState);
    });
  };

  getCurrentFrame = () => {
    return this.currentFrame;
  };

  setCurrentFrame = (frame: number) => {
    this.currentFrame = frame;
  };

  setPlayerRef = (ref: React.RefObject<any> | null) => {
    this.playerRef = ref;
  };

  getPlayerRef = () => {
    return this.playerRef;
  };

  seekToImage = (timelineItemExternalId: string) => {
    const state = this.get();
    const { video, animatedImages } = state;
    if (!video) return;

    const imageTimings = getCompositionTimings(
      video.compositionJson,
      animatedImages,
    ).mainScene.compositionImageTimings;

    const imageTiming = imageTimings.find(
      (timing) => timing.timelineItemExternalId === timelineItemExternalId,
    );

    if (!imageTiming) return;

    const frame = imageTiming.globalStartAtFrame;

    this.playerRef?.current?.seekTo(frame);
  };

  seekToElement = (elementExternalId: string, sceneType: SceneTypeDto) => {
    const compositionTimings = getCompositionTimings(
      this.get().video!.compositionJson,
      this.get().animatedImages,
    );

    const elementTiming = findElementTiming(
      compositionTimings,
      elementExternalId,
      sceneType,
    );

    const sceneSeekFrame = this.getSceneSeekFrame(
      compositionTimings,
      sceneType,
    );

    if (!elementTiming) return;

    const frame = Math.min(
      Math.max(
        sceneSeekFrame ?? 0,
        elementTiming.globalStartAtFrame + elementTiming.entryTransitionFrames,
      ),
      compositionTimings.durationInFrames - 1,
    );

    this.playerRef?.current?.seekTo(frame);
  };

  seekToScene = (sceneType: SceneTypeDto) => {
    const compositionTimings = getCompositionTimings(
      this.get().video!.compositionJson,
      this.get().animatedImages,
    );

    const frame = this.getSceneSeekFrame(compositionTimings, sceneType);

    if (frame === null) return;

    this.playerRef?.current?.seekTo(frame);
  };

  private getSceneSeekFrame = (
    compositionTimings: CompositionTimings,
    sceneType: SceneTypeDto,
  ) => {
    const sceneTimings = compositionTimings[sceneType];

    if (sceneTimings === null) return null;

    let frame: number;

    if (sceneType === "startScene") {
      frame = Math.min(
        sceneTimings.globalStartAtFrame,
        compositionTimings.durationInFrames - 1,
      );
    } else if (sceneType === "endScene") {
      const endSceneTimings = sceneTimings as EndSceneTiming;
      frame = Math.min(
        endSceneTimings.globalStartAtFrame +
          endSceneTimings.entryTransitionFrames,
        compositionTimings.durationInFrames - 1,
      );
    } else {
      const mainSceneTimings = sceneTimings as MainSceneTiming;
      frame = Math.min(
        mainSceneTimings.globalStartAtFrame +
          mainSceneTimings.entryTransitionFrames,
        compositionTimings.durationInFrames - 1,
      );
    }

    return frame;
  };

  refreshCreditBalance = async () => {
    await queryClient.invalidateQueries({
      queryKey: teamQueryKeys.getTeam(this.get().teamExternalId),
    });
  };

  initializeSlowMotion = async (data: InitializeSlowMotionDto) => {
    const animatedImage = await AnimatedImageApi.initializeSlowMotion(
      this.get().teamExternalId,
      data,
    );

    this.set((state) => {
      state.animatedImages[animatedImage.externalId] = animatedImage;
    });

    return animatedImage;
  };

  handleNewFailedAnimatedImages = (
    newAnimatedImages: Record<string, AnimatedImageDto>,
  ) => {
    const oldAnimatedImages = this.get().animatedImages;

    for (const oldExternalId in oldAnimatedImages) {
      const oldAnimatedImage = oldAnimatedImages[oldExternalId]!;
      const newAnimatedImage = newAnimatedImages[oldExternalId];

      if (
        newAnimatedImage?.status &&
        oldAnimatedImage.status !== "FAILED" &&
        newAnimatedImage.status === "FAILED"
      ) {
        this.refreshCreditBalance();
        return;
      }
    }
  };

  handleNewFailedSourceImages = (
    newSourceImages: Record<string, SourceImageDto>,
  ) => {
    const oldSourceImages = this.get().sourceImages;

    for (const oldExternalId in oldSourceImages) {
      const oldSourceImage = oldSourceImages[oldExternalId]!;
      const newSourceImage = newSourceImages[oldExternalId];

      if (
        newSourceImage?.status &&
        oldSourceImage.status !== "FAILED" &&
        newSourceImage.status === "FAILED"
      ) {
        this.refreshCreditBalance();
        return;
      }
    }
  };

  setIsMediaSelectorDialogOpen = (isOpen: boolean) => {
    this.set((state) => {
      state.overlaysTabState.mediaSelectorDialogState.isOpen = isOpen;
    });
  };

  addAssetMediaElements = (
    assetMedias: AssetMediaDto[],
    sceneType: SceneTypeDto,
  ) => {
    const compositionAspectRatio =
      this.get().video?.compositionJson.aspectRatio;

    if (!compositionAspectRatio) {
      throw new Error("Composition aspect ratio is not set");
    }

    const resolution = getResolution(compositionAspectRatio);
    const compositionAspectRatioNumber = parseAspectRatio(
      compositionAspectRatio,
    );
    const elementExternalIds: string[] = [];

    for (const assetMedia of assetMedias) {
      if (!assetMedia.width || !assetMedia.height) {
        console.error(
          `Asset media width or height is not set: ${assetMedia.externalId}`,
        );
        continue;
      }

      this.set((state) => {
        state.assetMedias[assetMedia.externalId] = {
          status: "COMPLETED",
          data: assetMedia,
        };
      });

      const mediaAspectRatio = assetMedia.width / assetMedia.height;

      let elementWidth: number;
      let elementHeight: number;

      if (mediaAspectRatio > compositionAspectRatioNumber) {
        elementWidth = Math.min(assetMedia.width, 500, resolution.width);
        elementHeight = elementWidth / mediaAspectRatio;
      } else {
        elementHeight = Math.min(assetMedia.height, 500, resolution.height);
        elementWidth = elementHeight * mediaAspectRatio;
      }

      const elementExternalId = crypto.randomUUID();
      elementExternalIds.push(elementExternalId);

      this.addElementToComposition(
        {
          externalId: elementExternalId,
          type: assetMedia.type,
          assetMediaExternalId: assetMedia.externalId,
          position: {
            type: "ABSOLUTE",
            x: Math.round((resolution.width - elementWidth) / 2),
            y: Math.round((resolution.height - elementHeight) / 2),
          },
          size: {
            type: "ABSOLUTE",
            w: Math.floor(elementWidth),
            h: Math.floor(elementHeight),
          },
          opacity: 100,
          cornerRadius: 0,
          border: null,
          shadow: null,
          enterAt: {
            timing: null,
            animation: null,
          },
          exitAt: {
            timing: null,
            animation: null,
          },
        },
        compositionAspectRatio,
        sceneType,
        false,
      );
    }

    if (elementExternalIds.length === 1) {
      this.updateOverlaysTabState((state) => {
        state.currentElement = {
          externalId: elementExternalIds[0]!,
          sceneType,
        };
      });
      this.seekToElement(elementExternalIds[0]!, sceneType);
    } else {
      this.updateOverlaysTabState((state) => {
        state.currentElement = null;
      });
    }
  };

  // Returns the typography a newly created text element should use: the font
  // last used during this session, an existing text element's font when there
  // is no session history, or the default font when there are no text elements.
  getNewTextTypography = (): ElementTypographyDto => {
    const typography =
      this.lastTextTypography ?? this.findExistingTextTypography();

    if (typography) {
      return {
        fontSize: typography.fontSize,
        fontWeight: typography.fontWeight,
        fontExternalId: typography.fontExternalId,
        fontStyle: typography.fontStyle,
      };
    }

    const font = this.getFontByExternalId(
      this.get().video!.defaultFontExternalId,
    );

    if (!font) {
      throw new Error("Default font is not loaded");
    }

    return {
      fontSize: 100,
      fontWeight: 400,
      fontExternalId: font.externalId,
      fontStyle: "normal",
    };
  };

  private findExistingTextTypography = (): ElementTypographyDto | null => {
    const composition = this.get().video?.compositionJson;
    if (!composition) {
      return null;
    }

    const textElement = getAllElements(composition).find(
      ({ element }) => element.type === "TEXT",
    )?.element;

    return textElement?.type === "TEXT" ? textElement.typography : null;
  };

  rememberTextTypography = (typography: ElementTypographyDto) => {
    this.lastTextTypography = {
      fontSize: typography.fontSize,
      fontExternalId: typography.fontExternalId,
      fontWeight: typography.fontWeight,
      fontStyle: typography.fontStyle,
    };
  };

  addElementToComposition = (
    element: ElementDto,
    aspectRatio: string,
    sceneType: SceneTypeDto,
    seekToElement: boolean = true,
  ) => {
    if (element.type === "TEXT") {
      this.rememberTextTypography(element.typography);
    }

    this.set((state) => {
      const composition = state.video?.compositionJson;

      if (!composition) {
        throw new Error("Composition is not present");
      }

      if (!composition.elements) {
        composition.elements = {};
      }

      if (!composition.elements[aspectRatio]) {
        composition.elements[aspectRatio] = [];
      }

      if (sceneType === "mainScene") {
        composition.elements[aspectRatio] = [
          element,
          ...composition.elements[aspectRatio],
        ];
      } else {
        composition[sceneType]![aspectRatio]!.elements = [
          element,
          ...composition[sceneType]![aspectRatio]!.elements,
        ];
      }
    });

    if (seekToElement) {
      this.seekToElement(element.externalId, sceneType);
    }

    this.syncVideoDebounced();
  };

  toggleSceneInComposition = (
    sceneType: "startScene" | "endScene",
    isEnabled: boolean,
  ) => {
    const aspectRatio = this.get().video?.compositionJson.aspectRatio;

    if (!aspectRatio) {
      throw new Error("Aspect ratio is not present");
    }

    this.set((state) => {
      const composition = state.video?.compositionJson;

      if (!composition) {
        throw new Error("Composition is not present");
      }

      if (!composition[sceneType]) {
        composition[sceneType] = {};
      }

      if (!isEnabled) {
        delete composition[sceneType][aspectRatio];
      } else if (sceneType === "startScene") {
        composition[sceneType][aspectRatio] = {
          duration: {
            type: "SECOND_TIMING",
            seconds: 5,
          },
          elements: [],
          exitTransition: {
            kind: "FADE_OUT",
            direction: "FROM_TOP",
            duration: 0.7,
            easing: "EASE_IN_OUT",
          },
        };
      } else {
        composition[sceneType][aspectRatio] = {
          duration: {
            type: "SECOND_TIMING",
            seconds: 5,
          },
          elements: [],
          entryTransition: {
            kind: "FADE_IN",
            direction: "FROM_TOP",
            duration: 0.7,
            easing: "EASE_IN_OUT",
          },
        };
      }
    });

    this.syncVideoDebounced();
  };

  updateScene = (
    sceneType: "startScene" | "endScene",
    updater: (currentScene: StartSceneDto | EndSceneDto) => void,
  ) => {
    this.set((state) => {
      const aspectRatio = state.video?.compositionJson.aspectRatio;

      if (!aspectRatio) {
        throw new Error("Aspect ratio is not present");
      }

      const scene = state.video?.compositionJson?.[sceneType]?.[aspectRatio];

      if (!scene) {
        throw new Error("Scene is not present");
      }

      updater(scene);
    });

    this.syncVideoDebounced();
  };

  onStateChanged() {
    this.refreshAssetMedias();
    this.cacheCompositionChange();
  }

  refreshAssetMedias = async () => {
    const composition = this.get().video?.compositionJson;

    if (!composition) {
      return;
    }

    const assetMediaExternalIds = Object.keys(this.get().assetMedias);

    const idsToFetch = getAssetMediaExternalIds(
      composition,
      assetMediaExternalIds,
    );

    if (idsToFetch.length === 0) {
      return;
    }

    this.set((state) => {
      idsToFetch.forEach((assetMediaExternalId) => {
        state.assetMedias[assetMediaExternalId] = {
          status: "PENDING",
        };
      });
    });

    const assetMediasByExternalIds = await AssetMediaApi.getAssetMediasByIds(
      this.get().teamExternalId,
      idsToFetch,
      "COMPLETED",
    );

    this.set((state) => {
      for (const externalId of idsToFetch) {
        const assetMedia = assetMediasByExternalIds[externalId];

        if (assetMedia) {
          state.assetMedias[externalId] = {
            status: "COMPLETED",
            data: assetMedia,
          };
        } else {
          state.assetMedias[externalId] = {
            status: "FAILED",
          };
        }
      }
    });
  };

  handleElementDrag = (event: DragEndEvent, sceneType: SceneTypeDto) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const elements = getElementsByScene(
      this.get().video?.compositionJson,
      sceneType,
    );
    const aspectRatio = this.get().video?.compositionJson.aspectRatio;

    if (elements.length === 0 || !aspectRatio) {
      return;
    }

    const oldIndex = elements.findIndex(
      (item) => item.externalId === active.id,
    );
    const newIndex = elements.findIndex((item) => item.externalId === over.id);

    this.setSceneElements(sceneType, arrayMove(elements, oldIndex, newIndex));
  };

  setSceneElements = (
    sceneType: SceneTypeDto,
    elements: ElementDto[],
    sync: boolean = true,
  ) => {
    const aspectRatio = this.get().video?.compositionJson.aspectRatio;

    if (!aspectRatio) {
      return;
    }

    this.set((state) => {
      if (sceneType === "mainScene") {
        state.video!.compositionJson.elements![aspectRatio] = elements;
      } else {
        state.video!.compositionJson[sceneType]![aspectRatio]!.elements =
          elements;
      }
    });

    if (sync) {
      this.syncVideoDebounced();
    }
  };

  addOverlayTemplateToComposition = (template: OverlayTemplateDto) => {
    this.set((state) => {
      const composition = state.video!.compositionJson;

      const newElements = template.content.elements ?? {};
      const newStartScene = template.content.startScene ?? {};
      const newEndScene = template.content.endScene ?? {};

      if (!composition.elements) {
        composition.elements = {};
      }

      if (!composition.startScene) {
        composition.startScene = {};
      }

      if (!composition.endScene) {
        composition.endScene = {};
      }

      for (const aspectRatio in newElements) {
        if (!composition.elements[aspectRatio]) {
          composition.elements[aspectRatio] = newElements[aspectRatio] ?? [];
        } else {
          composition.elements[aspectRatio] = [
            ...(newElements[aspectRatio] ?? []),
            ...composition.elements[aspectRatio],
          ];
        }
      }

      for (const aspectRatio in newStartScene) {
        const existingScene = composition.startScene[aspectRatio];
        const existingElements = existingScene?.elements ?? [];

        composition.startScene[aspectRatio] = newStartScene[aspectRatio]!;

        if (existingElements.length > 0) {
          composition.startScene[aspectRatio]!.elements = [
            ...composition.startScene[aspectRatio]!.elements,
            ...existingElements,
          ];
        }
      }

      for (const aspectRatio in newEndScene) {
        const existingScene = composition.endScene[aspectRatio];
        const existingElements = existingScene?.elements ?? [];

        composition.endScene[aspectRatio] = newEndScene[aspectRatio]!;

        if (existingElements.length > 0) {
          composition.endScene[aspectRatio]!.elements = [
            ...composition.endScene[aspectRatio]!.elements,
            ...existingElements,
          ];
        }
      }
    });

    void this.loadMissingCompositionFonts();

    this.syncVideoDebounced();
  };

  private loadMissingCompositionFonts = async () => {
    const state = this.get();
    const composition = state.video?.compositionJson;
    if (!composition) return;

    const referencedIds = getFontExternalIds(composition);
    const existingIds = new Set(
      state.compositionFonts.map((f) => f.externalId),
    );
    const missingIds = referencedIds.filter((id) => !existingIds.has(id));
    if (missingIds.length === 0) return;

    const fonts = await EditorApi.getFontsByExternalIds(
      state.teamExternalId,
      missingIds,
    );

    this.set((s) => {
      const byId = new Map(s.compositionFonts.map((f) => [f.externalId, f]));
      for (const font of Object.values(fonts)) byId.set(font.externalId, font);
      s.compositionFonts = Array.from(byId.values());
    });
  };

  suggestAnimations = async () => {
    const timelineItems =
      this.get().video?.compositionJson.mainSceneTimeline ?? [];
    const animationFormItems = this.get().animationForm.items;

    const itemsForSuggestions = timelineItems.filter(
      (item) =>
        item.externalId in animationFormItems &&
        item.startSourceImageExternalId !== undefined,
    );

    const analysisResults = await SourceImageApi.getImageAnalysisResults(
      this.get().teamExternalId,
      {
        sourceImageExternalIds: itemsForSuggestions.map(
          (item) => item.startSourceImageExternalId!,
        ),
      },
    );

    this.set((state) => {
      let previousAnimation: CameraMovement | undefined;

      for (const timelineItem of itemsForSuggestions) {
        const analysisResult =
          analysisResults[timelineItem.startSourceImageExternalId!];

        if (!analysisResult) {
          continue;
        }

        const suggestedAnimation = this.determineAnimationType(
          analysisResult,
          previousAnimation,
        );

        if (suggestedAnimation) {
          state.animationForm.items[timelineItem.externalId]! = {
            promptType: suggestedAnimation,
          };
          previousAnimation = suggestedAnimation;
        }
      }
    });
  };

  private determineAnimationType(
    analysis: ImageAnalysisResult,
    previousAnimation?: CameraMovement,
  ): CameraMovement {
    const primaryChoice = this.getPrimaryAnimationChoice(analysis);

    if (!previousAnimation || primaryChoice !== previousAnimation) {
      return primaryChoice;
    }

    const fallbackAnimations: CameraMovement[] = ["PUSH_IN", "ROTATE"];

    return fallbackAnimations.filter(
      (animation) => animation !== previousAnimation,
    )[0]!;
  }

  private getPrimaryAnimationChoice(
    analysis: ImageAnalysisResult,
  ): CameraMovement {
    if (analysis.isAerial) {
      return "ROTATE";
    }

    if (analysis.isOutside && analysis.isArchitectural) {
      return "DRONE_UP";
    }

    if (analysis.isWideShot && !analysis.isOutside) {
      return "ROTATE";
    }

    return "PUSH_IN";
  }

  private cacheCompositionChange = () => {
    if (this.skipNextCompositionChange) {
      this.skipNextCompositionChange = false;
      return;
    }

    const currentComposition = this.get().video?.compositionJson;

    if (!currentComposition) {
      return;
    }

    const historyComposition =
      this.compositionHistory[this.compositionHistoryIndex];

    const isSame = historyComposition === currentComposition;

    if (isSame) {
      return;
    }

    if (this.compositionHistoryIndex !== 0) {
      this.compositionHistory = this.compositionHistory.slice(
        this.compositionHistoryIndex,
      );
    }

    this.compositionHistoryIndex = 0;

    const currentTime = Date.now();

    if (currentTime - this.latestCompositionChange > 500) {
      this.compositionHistory.unshift(currentComposition);
    } else {
      this.compositionHistory[0] = currentComposition;
    }

    this.compositionHistory = this.compositionHistory.slice(0, 50);
    this.latestCompositionChange = currentTime;
  };

  private undoCompositionChange = (event: KeyboardEvent) => {
    if (
      isActiveElementInput() ||
      this.get().creativeProjectSheetState.isSheetOpen
    ) {
      return;
    }

    const key = event.key.toLowerCase();
    const isCtrl = event.ctrlKey || event.metaKey;
    const isShift = event.shiftKey;

    if (key !== "z" || !isCtrl || isShift) {
      return;
    }

    event.stopPropagation();
    event.preventDefault();

    const index = this.compositionHistoryIndex + 1;

    const composition = this.compositionHistory[index];

    if (!composition) {
      return;
    }

    this.skipNextCompositionChange = true;
    this.compositionHistoryIndex = index;

    this.set((state) => {
      state.video!.compositionJson = composition;
    });

    this.syncVideoDebounced();
  };

  private redoCompositionChange = (event: KeyboardEvent) => {
    if (
      isActiveElementInput() ||
      this.get().creativeProjectSheetState.isSheetOpen
    ) {
      return;
    }

    const key = event.key.toLowerCase();
    const isCtrl = event.ctrlKey || event.metaKey;
    const isShift = event.shiftKey;

    if (key !== "z" || !isCtrl || !isShift) {
      return;
    }

    event.stopPropagation();
    event.preventDefault();

    const index = this.compositionHistoryIndex - 1;

    if (index < 0) {
      return;
    }

    const composition = this.compositionHistory[index];

    if (!composition) {
      return;
    }

    this.skipNextCompositionChange = true;
    this.compositionHistoryIndex = index;

    this.set((state) => {
      state.video!.compositionJson = composition;
    });

    this.syncVideoDebounced();
  };
}
